import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { useCampaigns, useDeleteCampaign, useCampaignTags } from "../hooks";
import { Spinner } from "../spinner";
import { CampaignCard } from "./card";
import { ConfirmModal } from "./confirm-modal";
import { SaveCampaignModal } from "../save-campaign-modal";
import { Button } from "../button";
import { Dropdown } from "../dropdown";

export interface CampaignItem {
  id: string;
  type: string;
  name: string;
  user_id?: number;
  avatar_url?: string;
  logo_url?: string;
  item_type: string;
  // Candidat fields
  project_address?: string;
  project_contribution?: number;
  updated_at?: string;
  // Enseigne fields
  chalandise?: string;
  surface_display?: string;
  contribution?: number;
  // Ville fields
  inhabitants?: number;
  sectors?: string[];
  installation_aids?: string[];
  // Location fields
  address?: string;
  surface?: number;
  window?: number;
  sidewalk_width?: number;
}

export interface Campaign {
  id: number;
  title: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
  campaign_items: CampaignItem[];
}

const ITEMS_PER_PAGE = 3;

export function SavedCampaignsTable() {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTag, setSelectedTag] = useState<string | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);

  const { data: response, isLoading } = useCampaigns(
    currentPage,
    ITEMS_PER_PAGE,
    selectedTag,
  );
  const { data: tags, isLoading: tagsLoading } = useCampaignTags();
  const deleteCampaignMutation = useDeleteCampaign();

  const handleDelete = (campaignId: number) => {
    setConfirmDelete(campaignId);
  };

  const confirmDeleteAction = async () => {
    if (confirmDelete === null) return;

    try {
      await deleteCampaignMutation.mutateAsync(confirmDelete);
      // Reset to page 1 if we deleted the last item on a page
      if (response?.campaigns.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
      setConfirmDelete(null);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleEdit = (campaign: Campaign) => {
    setEditingCampaign(campaign);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="md" variant="default" />
      </div>
    );
  }

  if (!response || !response.campaigns || response.campaigns.length === 0) {
    return (
      <div className="card p-8 text-center text-slate-500">
        <p>Aucun projet enregistré pour le moment.</p>
        <p className="text-sm mt-2">
          Sélectionnez au moins 2 profils ci-dessus pour créer votre premier
          projet.
        </p>
      </div>
    );
  }

  const { campaigns, pagination } = response;
  const totalPages = pagination.total_pages;

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="space-y-4 pt-4">
      {/* Header with Tag Filter */}
      <div className="flex items-center justify-between pb-6">
        <h3 className="font-semibold text-slate-800 text-2xl">
          Projets enregistrés
        </h3>

        {/* Tag Filter Dropdown */}
        {tags && tags.length > 0 && (
          <Dropdown
            options={tags.map((tag) => ({ value: tag, label: tag }))}
            value={selectedTag}
            onChange={(value) => {
              setSelectedTag(value);
              setCurrentPage(1);
            }}
            allLabel="Tous les tags"
            icon={Filter}
          />
        )}
      </div>

      {/* Campaigns */}
      <div className="space-y-6">
        {campaigns.map((campaign) => (
          <CampaignCard
            key={campaign.id}
            campaign={campaign}
            onDelete={handleDelete}
            onEdit={handleEdit}
            isDeleting={deleteCampaignMutation.isPending}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center pt-12">
          <nav
            className="inline-flex items-center gap-6"
            role="pagination"
            aria-label="pager"
          >
            <Button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              variant="white"
              size="large"
              icon={ChevronLeft}
              aria-label="Previous page"
              className="rounded-lg"
            />
            <div className="text-sm text-slate-600 font-medium">
              Page {currentPage} sur {totalPages}
            </div>
            <Button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              variant="white"
              size="large"
              icon={ChevronRight}
              aria-label="Next page"
              className="rounded-lg"
            />
          </nav>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete !== null && (
        <ConfirmModal
          title="Supprimer le projet"
          message="Êtes-vous sûr de vouloir supprimer ce projet ? Cette action est irréversible."
          confirmText="Supprimer"
          cancelText="Annuler"
          variant="danger"
          onConfirm={confirmDeleteAction}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Edit Campaign Modal */}
      {editingCampaign && (
        <SaveCampaignModal
          campaign={editingCampaign}
          onClose={() => setEditingCampaign(null)}
          onSuccess={() => setEditingCampaign(null)}
        />
      )}
    </div>
  );
}
